import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Star, BadgeCheck } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Stars = ({ value, size = "w-4 h-4" }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= Math.round(value) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} />
    ))}
  </span>
);

export const HerbReviews = ({ herbId, herbName }) => {
  const { isAuthenticated, openAuth } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/store/reviews/${herbId}`).then(({ data }) => setData(data)).catch(() => setData({ reviews: [], count: 0 }));
    if (isAuthenticated) {
      axios.get(`${API}/store/my-review/${herbId}`).then(({ data }) => {
        setMine(data);
        if (data.review) { setRating(data.review.rating); setComment(data.review.comment || ""); }
      }).catch(() => {});
    }
  }, [herbId, isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!rating) { toast.error("Please pick a star rating."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/store/reviews`, { herb_id: herbId, rating, comment: comment || null });
      toast.success(mine?.review ? "Review updated — thank you!" : "Review published — thank you!");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review.");
    } finally { setSaving(false); }
  };

  if (!data) return null;

  return (
    <div className="mb-12" id="reviews" data-testid="herb-reviews-section">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <h2 className="font-playfair text-2xl font-bold text-gray-900">Customer Reviews</h2>
        {data.count > 0 && (
          <span className="flex items-center gap-2 text-sm text-gray-600" data-testid="herb-reviews-average">
            <Stars value={data.average} /> <b>{data.average}</b> · {data.count} review{data.count === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Write / edit review */}
      {!isAuthenticated ? (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-6 text-sm text-gray-600 flex items-center justify-between gap-3 flex-wrap">
          <span>Bought {herbName}? Log in to leave a verified review.</span>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => openAuth("login")} data-testid="review-login-btn">Log in</Button>
        </div>
      ) : mine && !mine.can_review ? (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-6 text-sm text-gray-500" data-testid="review-not-eligible">
          Reviews are limited to verified buyers — order {herbName} and you can rate it after checkout.
        </div>
      ) : mine?.can_review ? (
        <div className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-5 mb-6" data-testid="review-form">
          <p className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-1.5">
            <BadgeCheck className="w-4 h-4 text-emerald-600" /> {mine.review ? "Update your review" : "You bought this — rate it!"}
          </p>
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
                      onClick={() => setRating(i)} data-testid={`review-star-${i}`} aria-label={`${i} stars`}>
                <Star className={`w-7 h-7 transition-colors ${i <= (hover || rating) ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} />
              </button>
            ))}
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={1000}
                    placeholder="How did it work for you? (optional)" data-testid="review-comment-input"
                    className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:outline-none focus:border-primary bg-white" />
          <Button size="sm" onClick={submit} disabled={saving} className="rounded-full mt-2" data-testid="review-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : mine.review ? "Update review" : "Publish review"}
          </Button>
        </div>
      ) : null}

      {/* Review list */}
      {data.reviews.length === 0 ? (
        <p className="text-sm text-gray-400" data-testid="reviews-empty">No reviews yet — be the first verified buyer to rate {herbName}.</p>
      ) : (
        <div className="space-y-4">
          {data.reviews.map((r) => (
            <div key={r.id} className="border border-gray-100 rounded-2xl p-5 bg-white shadow-sm" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
                <div className="flex items-center gap-2">
                  <Stars value={r.rating} size="w-3.5 h-3.5" />
                  <span className="font-semibold text-sm text-gray-900">{r.reviewer_name}</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                    <BadgeCheck className="w-3 h-3" /> Verified buyer
                  </span>
                </div>
                <span className="text-xs text-gray-400">{r.created_at?.slice(0, 10)}</span>
              </div>
              {r.comment && <p className="text-sm text-gray-600 leading-relaxed">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
